import { create } from "zustand";
import { updateArticleReq, deleteArticleReq } from "@/api/adminApi";
import { ArticleType } from "@/types";
import { ArticleForm } from "@/pages/NewsPage/components/EditArticle.tsx";
import { getArticlesReq } from "@/api/userApi";
import LocalStorageService from "@/utils/localStorageService";

type State = {
  news: ArticleType[];
  hasNewArticles: boolean;
};

type Actions = {
  getNews: (search: string) => void;
  createArticle: (newArticle: ArticleType) => void;
  updateArticle: (
    articleId: number,
    updatedArticle: ArticleForm
  ) => Promise<void>;
  deleteArticle: (articleId: number) => Promise<void>;
  checkForNewArticles: () => void;
  markArticlesAsViewed: () => void;
  clearArticleTracking: () => void;
};

const LAST_CHECKED_ARTICLE_KEY = "lastCheckedArticleId";

export const useNewsStore = create<State & Actions>((set, get) => ({
  news: [],
  hasNewArticles: false,

  // Fetch news with search
  getNews: async (search) => {
    try {
      const response = await getArticlesReq(search);
      set(() => ({
        news: response?.data?.articles,
      }));
      get().checkForNewArticles();
    } catch (e) {
      console.error(e);
    }
  },

  // Create article with optimistic update
  createArticle: (newArticle) => {
    // Optimistically add the new article to the state
    set((state) => ({
      news: [newArticle, ...state.news],
    }));
  },

  // Update article with optimistic update
  updateArticle: async (articleId, updatedArticle) => {
    const previousNews = get().news;

    // Optimistically update the article in the state
    set((state) => ({
      news: state.news.map((article) =>
        article.id === articleId ? { ...article, ...updatedArticle } : article
      ),
    }));

    try {
      // Call API to update article
      await updateArticleReq(articleId, {
        title: updatedArticle?.title,
        content: updatedArticle?.content,
        dateStamp: updatedArticle?.dateStamp,
      });
    } catch (e) {
      console.error(e);
      // Revert the state if API call fails
      set(() => ({
        news: previousNews,
      }));
    }
  },

  // Delete article with optimistic update
  deleteArticle: async (articleId) => {
    const previousNews = get().news;

    // Optimistically remove the article from the state
    set((state) => ({
      news: state.news.filter((article) => article.id !== articleId),
    }));

    try {
      // Call API to delete article
      await deleteArticleReq(articleId);
    } catch (e) {
      console.error(e);
      // Revert the state if API call fails
      set(() => ({
        news: previousNews,
      }));
    }
  },

  // Check if there are new articles
  checkForNewArticles: () => {
    const news = get().news;

    if (!news || news.length === 0) {
      set({ hasNewArticles: false });
      return;
    }

    const lastCheckedId = LocalStorageService.getItem(LAST_CHECKED_ARTICLE_KEY);

    // If no article has been checked before, consider there are new articles
    if (!lastCheckedId) {
      set({ hasNewArticles: true });
      return;
    }

    // Find the highest article ID in the current list
    const maxArticleId = Math.max(...news.map((article) => article.id));

    // If the highest ID is greater than the last checked ID, there are new articles
    set({ hasNewArticles: maxArticleId > Number.parseInt(lastCheckedId, 10) });
  },

  // Mark articles as viewed
  markArticlesAsViewed: () => {
    const news = get().news;

    if (!news || news.length === 0) {
      return;
    }

    const maxArticleId = Math.max(...news.map((article) => article.id));
    LocalStorageService.setItem(LAST_CHECKED_ARTICLE_KEY, maxArticleId.toString());
    set({ hasNewArticles: false });
  },

  // Clear article tracking (called on logout if needed)
  clearArticleTracking: () => {
    LocalStorageService.removeItem(LAST_CHECKED_ARTICLE_KEY);
    set({ hasNewArticles: false });
  },
}));
